import { useState } from 'react';
import { Badge, Button, Group, Select, Table, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  POA_STATUS_COLORS,
  POA_STATUS_LABELS,
  powersOfAttorneyApi,
  type PowerOfAttorneyStatus,
} from '../api/powers-of-attorney';
import { useAuth } from '../auth/AuthContext';
import { RegistryPage } from '../components/RegistryPage';

const STATUS_OPTIONS = Object.entries(POA_STATUS_LABELS).map(([value, label]) => ({ value, label }));

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('ru-RU');
}

export function PowersOfAttorneyPage() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canEdit = hasRole('ADMIN', 'ACCOUNTANT', 'MANAGER');

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [status, setStatus] = useState<PowerOfAttorneyStatus | null>(null);

  const list = useQuery({
    queryKey: ['powers-of-attorney', { search: debouncedSearch, status }],
    queryFn: () => powersOfAttorneyApi.list({ search: debouncedSearch, status: status ?? undefined }),
  });

  const items = list.data?.items ?? [];

  return (
    <RegistryPage
      title="Доверенности"
      action={
        canEdit && (
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => navigate('/powers-of-attorney/new')}
          >
            Выписать доверенность
          </Button>
        )
      }
      description={
        <Text size="sm" c="dimmed">
          Доверенность М-2 на получение товарно-материальных ценностей у поставщика. Выписывается на
          сотрудника с заполненными паспортными данными, по умолчанию на 15 дней.
        </Text>
      }
      filters={
        <Group>
          <TextInput
            placeholder="Поиск по номеру, сотруднику или поставщику"
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={340}
          />
          <Select
            placeholder="Все статусы"
            data={STATUS_OPTIONS}
            value={status}
            onChange={(v) => setStatus(v as PowerOfAttorneyStatus | null)}
            clearable
            w={180}
          />
        </Group>
      }
    >
      <Table striped highlightOnHover verticalSpacing="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Номер</Table.Th>
            <Table.Th>Дата</Table.Th>
            <Table.Th>Действует по</Table.Th>
            <Table.Th>Организация</Table.Th>
            <Table.Th>Кому выдана</Table.Th>
            <Table.Th>Поставщик</Table.Th>
            <Table.Th>Статус</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((item) => (
            <Table.Tr
              key={item.id}
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/powers-of-attorney/${item.id}`)}
            >
              <Table.Td>
                <Text size="sm" fw={500}>
                  № {item.number}
                </Text>
              </Table.Td>
              <Table.Td>{formatDate(item.date)}</Table.Td>
              <Table.Td>{formatDate(item.validUntil)}</Table.Td>
              <Table.Td>{item.organization.shortName}</Table.Td>
              <Table.Td>{item.employeeName}</Table.Td>
              <Table.Td>{item.counterparty.shortName}</Table.Td>
              <Table.Td>
                <Badge color={POA_STATUS_COLORS[item.status]} variant="light">
                  {POA_STATUS_LABELS[item.status]}
                </Badge>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {!list.isLoading && items.length === 0 && (
        <Text c="dimmed" size="sm" ta="center" py="xl">
          Доверенности не найдены
        </Text>
      )}
    </RegistryPage>
  );
}
